import React, { useRef, useState } from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useGetBankDocumentCompaniesLookupQuery } from '../Service/companiesLookupApi';
import { useGetBankDocumentBankAccountsLookupQuery } from '../Service/bankAccountsLookupApi';
import type { BankDocumentBankAccountLookup } from '../Service/bankAccountsLookupMapper';
import {
  normalizeAccountNumber,
  tryExtractAccountNumber,
} from '../Utils/accountNumberDetection';

export interface ImportCsvDialogSubmitArgs {
  file: File;
  accountNumber: string;
}

interface ImportCsvDialogProps {
  open: boolean;
  isImporting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (args: ImportCsvDialogSubmitArgs) => void;
}

const ImportCsvDialog: React.FC<ImportCsvDialogProps> = ({
  open,
  isImporting,
  errorMessage,
  onClose,
  onSubmit,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [selectedAccount, setSelectedAccount] = useState<BankDocumentBankAccountLookup | null>(
    null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [detectedAccountNumber, setDetectedAccountNumber] = useState<string | null>(null);
  const [isDetectingAccountNumber, setIsDetectingAccountNumber] = useState(false);

  const { companies, isLoading: isLoadingCompanies } = useGetBankDocumentCompaniesLookupQuery();
  const { accounts, isLoading: isLoadingAccounts } = useGetBankDocumentBankAccountsLookupQuery(
    selectedCompanyId !== '' ? selectedCompanyId : undefined
  );

  const selectedCompany =
    selectedCompanyId !== '' ? companies.find((company) => company.id === selectedCompanyId) : null;

  const handleCompanyChange = (companyId: number | '') => {
    setSelectedCompanyId(companyId);
    setSelectedAccount(null);
    setDetectedAccountNumber(null);
  };

  const handleAccountChange = (accountId: number | '') => {
    const found = accounts.find((a) => a.id === accountId) ?? null;
    setSelectedAccount(found);
  };

  const handleChooseFile = () => {
    setFileError(null);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileError('Dozwolony jest wyłącznie plik CSV.');
      setSelectedFile(null);
      return;
    }

    setFileError(null);
    setSelectedFile(file);

    setIsDetectingAccountNumber(true);
    try {
      const rawContent = await file.text();
      const detected = tryExtractAccountNumber(rawContent, file.name);
      setDetectedAccountNumber(detected);
    } catch {
      setDetectedAccountNumber(null);
    } finally {
      setIsDetectingAccountNumber(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedFile || !selectedAccount) {
      return;
    }

    onSubmit({ file: selectedFile, accountNumber: selectedAccount.accountNumber });
  };

  const handleClose = () => {
    if (isImporting) {
      return;
    }

    setSelectedCompanyId('');
    setSelectedAccount(null);
    setSelectedFile(null);
    setFileError(null);
    setDetectedAccountNumber(null);
    onClose();
  };

  const selectedAccountNormalized = selectedAccount
    ? normalizeAccountNumber(selectedAccount.accountNumber)
    : null;
  const isDetectedAccountMatching =
    Boolean(detectedAccountNumber) &&
    Boolean(selectedAccountNormalized) &&
    detectedAccountNumber === selectedAccountNormalized;

  const isSubmitDisabled = isImporting || !selectedCompanyId || !selectedAccount || !selectedFile;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Import wyciągu bankowego CSV</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Krok 1: Firma */}
          <FormControl fullWidth disabled={isImporting}>
            <InputLabel id="import-company-label">Firma</InputLabel>
            <Select
              labelId="import-company-label"
              label="Firma"
              value={selectedCompanyId}
              onChange={(e) => handleCompanyChange(e.target.value as number | '')}
            >
              {isLoadingCompanies ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Ładowanie...
                </MenuItem>
              ) : (
                companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Krok 2: Rachunek bankowy */}
          <FormControl fullWidth disabled={isImporting || !selectedCompanyId}>
            <InputLabel id="import-account-label">Rachunek bankowy</InputLabel>
            <Select
              labelId="import-account-label"
              label="Rachunek bankowy"
              value={selectedAccount?.id ?? ''}
              onChange={(e) => handleAccountChange(e.target.value as number | '')}
            >
              {isLoadingAccounts ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Ładowanie rachunków...
                </MenuItem>
              ) : accounts.length === 0 ? (
                <MenuItem disabled>Brak rachunków dla tej firmy</MenuItem>
              ) : (
                accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedAccount && selectedCompany && (
            <Alert severity="info">
              Rachunek: <strong>{selectedAccount.accountNumber}</strong> &bull; Firma:{' '}
              <strong>{selectedCompany.name}</strong>
            </Alert>
          )}

          {selectedFile && isDetectingAccountNumber && (
            <Alert severity="info">Trwa odczyt numeru rachunku z pliku CSV…</Alert>
          )}

          {selectedFile && detectedAccountNumber && !isDetectingAccountNumber && (
            <Alert severity={isDetectedAccountMatching ? 'success' : 'warning'}>
              Wykryty rachunek z CSV: <strong>{detectedAccountNumber}</strong>
              {selectedAccount && (
                <> &bull; {isDetectedAccountMatching ? 'zgodny' : 'różny'} z wybranym rachunkiem</>
              )}
            </Alert>
          )}

          {selectedFile && !detectedAccountNumber && !isDetectingAccountNumber && (
            <Alert severity="warning">
              Nie wykryto jednoznacznie numeru rachunku w pliku. Zweryfikuj ręcznie wybrany
              rachunek.
            </Alert>
          )}

          {/* Krok 3: Plik CSV */}
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={handleChooseFile}
              disabled={isImporting || !selectedAccount}
            >
              {selectedFile ? 'Zmień plik CSV' : 'Wybierz plik CSV'}
            </Button>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Wybrany plik: <strong>{selectedFile.name}</strong> (
                {(selectedFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
            {fileError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {fileError}
              </Alert>
            )}
          </Box>

          {/* Błąd importu */}
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isImporting}>
          Anuluj
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          startIcon={isImporting ? <CircularProgress size={16} /> : <FileUploadIcon />}
        >
          {isImporting ? 'Importowanie...' : 'Importuj'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportCsvDialog;
